import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoArrowBack, IoAttach } from "react-icons/io5";
import { ProfilePicture } from "../components/CommonComponents";
import { useChat } from "../context/ChatContext";
import { useSession } from "../SessionContext";
import supabase from "../utils/supabase";
import getAvatarUrl from "../utils/dicebear";
import { format } from "timeago.js";

function Chatting() {
  const { id: chatId } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const [message, setMessage] = useState("");
  const { currentChat, setCurrentChat, loadMessages } = useChat();
  const messagesEndRef = useRef(null);
  const [otherUserStatus, setOtherUserStatus] = useState(null);

  const getUserStatus = async (userId) => {
    const { data } = await supabase
      .from("user_status")
      .select("*")
      .eq("user_id", userId)
      .single();
    return data;
  };

  const markMessagesAsRead = async (chatId, currentUserId) => {
    try {
      await supabase
        .from("messages")
        .update({ is_read: true })
        .eq("chat_id", chatId)
        .eq("receiver_id", currentUserId)
        .eq("is_read", false);
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  useEffect(() => {
    const loadChatData = async () => {
      try {
        const { data: chat } = await supabase
          .from("chats")
          .select(
            `
            *,
            user1_info:public_profiles!chats_user1_fkey(id, name, user_type),
            user2_info:public_profiles!chats_user2_fkey(id, name, user_type)
          `
          )
          .eq("id", chatId)
          .single();

        const messages = await loadMessages(chatId);
        setCurrentChat({ ...chat, messages });

        // Set up real-time subscription for this specific chat
        const channel = supabase.channel(`chat:${chatId}`);
        const subscription = channel
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "messages",
              filter: `chat_id=eq.${chatId}`,
            },
            async () => {
              const updatedMessages = await loadMessages(chatId);
              setCurrentChat((prev) => ({
                ...prev,
                messages: updatedMessages,
              }));
            }
          )
          .subscribe();

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error loading chat:", error);
        navigate("/chats");
      }
    };

    loadChatData();
  }, [chatId]);

  useEffect(() => {
    if (currentChat) {
      const otherUserId = getOtherUser()?.id;

      // Mark messages as read when chat is opened
      markMessagesAsRead(chatId, session.user.id);

      // Get and subscribe to other user's status
      const loadStatus = async () => {
        const status = await getUserStatus(otherUserId);
        setOtherUserStatus(status);
      };
      loadStatus();

      const statusChannel = supabase.channel(`user-status:${otherUserId}`);
      statusChannel
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "user_status",
            filter: `user_id=eq.${otherUserId}`,
          },
          async (payload) => {
            setOtherUserStatus(payload.new);
          }
        )
        .subscribe();

      return () => statusChannel.unsubscribe();
    }
  }, [currentChat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [currentChat?.messages]);

  const getOtherUser = () => {
    if (!currentChat) return null;
    return currentChat.user1 === session?.user?.id
      ? currentChat.user2_info
      : currentChat.user1_info;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
      const messageText = message.trim();
      setMessage("");

      await supabase
        .from("messages")
        .insert({
          chat_id: chatId,
          sender_id: session.user.id,
          receiver_id:
            currentChat.user1 === session.user.id
              ? currentChat.user2
              : currentChat.user1,
          message: messageText,
        })
        .single();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "just now";

    // Parse the UTC timestamp and convert to local time
    const date = new Date(timestamp + "Z"); // Add 'Z' to treat as UTC
    return format(date);
  };

  const otherUser = getOtherUser();
  if (!currentChat || !otherUser) return null;

  return (
    <div className="page nav-exists flex flex-col h-[calc(100vh-64px)] bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 flex items-center space-x-4 cursor-pointer">
        <IoArrowBack
          className="cursor-pointer text-gray-600"
          size={24}
          onClick={() => navigate("/chats")}
        />
        <ProfilePicture
          // src={`https://api.dicebear.com/7.x/bottts/svg?seed=${otherUser.name}`}
          src={getAvatarUrl(otherUser.name)}
          size={40}
          profileUrl={`/profile/${otherUser.id}`}
        />
        <div>
          <h2 className="font-semibold">
            {otherUser.name}
            <span
              className={`ml-2 h-2 w-2 rounded-full inline-block ${
                otherUserStatus?.is_online ? "bg-green-500" : "bg-gray-400"
              }`}
            ></span>
          </h2>
          <p className="text-xs text-gray-500">
            {otherUserStatus?.is_online
              ? "Online"
              : otherUserStatus?.last_seen
              ? `Last seen ${format(new Date(otherUserStatus.last_seen))}`
              : ""}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {currentChat.messages?.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${
              msg.sender_id === session.user.id
                ? "justify-end"
                : "justify-start"
            }`}
          >
            <div
              className={`max-w-[70%] rounded-lg p-3 ${
                msg.sender_id === session.user.id
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-800"
              }`}
            >
              <p>{msg.message}</p>
              <div className="text-xs mt-1 opacity-70 flex justify-between items-center">
                <span>{formatMessageTime(msg.created_at)}</span>
                {msg.sender_id === session.user.id && (
                  <span className="ml-2">{msg.is_read ? "✓✓" : "✓"}</span>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white p-4 shadow-lg sticky bottom-0">
        <form onSubmit={handleSend} className="flex items-center space-x-4">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 rounded-lg border border-gray-200 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}

export default Chatting;
